// ConsultationModal.jsx
import React, { useState } from "react"
import { X, Phone, CheckCircle2 } from "lucide-react"
import { Trans, useTranslation } from "react-i18next"

const initialForm = {
  lead_name: "",
  lead_surname: "",
  lead_telephone: "",
  lead_email: "",
  comment: "",
}

const ConsultationModal = ({ open, onClose, onSubmit, toursCount }) => {
  const { t } = useTranslation()

  const [form, setForm] = useState(initialForm)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  if (!open) return null

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))

  const handleClose = () => {
    onClose()
    setTimeout(() => {
      setForm(initialForm)
      setError(null)
      setSuccess(false)
    }, 200)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.lead_telephone.trim()) {
      setError(t("saved_tours.consultation.phone_required"))
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await onSubmit(form)
      setSuccess(true)
    } catch (err) {
      setError(err?.message || t("saved_tours.consultation.submit_error"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={handleClose} />

      <div className="relative w-full md:max-w-md bg-white rounded-t-2xl md:rounded-2xl border border-[#E7EAEF] shadow-xl p-5 md:p-6">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#9AA5B4] hover:text-[#26303D] transition-colors"
        >
          <X size={18} />
        </button>

        {success ? (
          <div className="flex flex-col items-center text-center py-6 gap-3">
            <CheckCircle2 size={40} className="text-[#1E9E5B]" />
            <div className="text-[#1F2733] font-semibold text-base">
              {t("saved_tours.consultation.success_title")}
            </div>
            <div className="text-sm text-[#5A6779]">
              {t("saved_tours.consultation.success_message")}
            </div>
            <button
              onClick={handleClose}
              className="mt-2 text-sm text-white bg-[#26303D] rounded-lg px-4 py-2"
            >
              {t("saved_tours.consultation.close")}
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <Phone size={16} className="text-[#26303D]" />
              <h3 className="text-base font-semibold text-[#1F2733]">
                {t("saved_tours.consultation.title")}
              </h3>
            </div>
            <div className="text-sm text-[#9AA5B4] mb-4">
              {t("saved_tours.consultation.subtitle", {
                count: toursCount,
              })}
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-[#5A6779]">
                    {t("saved_tours.consultation.first_name")}
                  </label>
                  <input
                    required
                    value={form.lead_name}
                    onChange={handleChange("lead_name")}
                    className="border border-[#E7EAEF] rounded-lg px-3 py-2 text-sm text-[#1F2733] focus:outline-none focus:border-[#26303D]"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-[#5A6779]">
                    {t("saved_tours.consultation.last_name")}
                  </label>
                  <input
                    value={form.lead_surname}
                    onChange={handleChange("lead_surname")}
                    className="border border-[#E7EAEF] rounded-lg px-3 py-2 text-sm text-[#1F2733] focus:outline-none focus:border-[#26303D]"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-[#5A6779]">
                  {t("saved_tours.consultation.phone")}
                </label>
                <input
                  required
                  type="tel"
                  value={form.lead_telephone}
                  onChange={handleChange("lead_telephone")}
                  placeholder={t("saved_tours.consultation.phone_placeholder")}
                  className="border border-[#E7EAEF] rounded-lg px-3 py-2 text-sm text-[#1F2733] focus:outline-none focus:border-[#26303D]"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-[#5A6779]">
                  {t("saved_tours.consultation.email")}
                </label>
                <input
                  type="email"
                  value={form.lead_email}
                  onChange={handleChange("lead_email")}
                  className="border border-[#E7EAEF] rounded-lg px-3 py-2 text-sm text-[#1F2733] focus:outline-none focus:border-[#26303D]"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-[#5A6779]">
                  {t("saved_tours.consultation.comment")}
                </label>
                <textarea
                  rows={2}
                  value={form.comment}
                  onChange={handleChange("comment")}
                  className="border border-[#E7EAEF] rounded-lg px-3 py-2 text-sm text-[#1F2733] focus:outline-none focus:border-[#26303D] resize-none"
                />
              </div>

              {error && <div className="text-xs text-[#D14343]">{error}</div>}

              <button
                type="submit"
                disabled={submitting}
                className="mt-1 flex items-center justify-center gap-1.5 text-sm text-white bg-[#26303D] rounded-lg px-4 py-2.5 disabled:opacity-50"
              >
                {submitting
                  ? t("saved_tours.consultation.submitting")
                  : t("saved_tours.consultation.submit")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

export default ConsultationModal
